type EventSignupCheckoutOrder = {
  status?: string | null;
  total?: number | null;
  totalTaxes?: number | null;
  availableLoyaltyRewards?: unknown | null;
  loyaltyEnrollmentOffer?: unknown | null;
};

type Params<TOrder extends EventSignupCheckoutOrder> = {
  orders: readonly TOrder[];
  registrationSubtotal: number;
  isDraftSignUp: boolean;
};

type State<TOrder extends EventSignupCheckoutOrder> = {
  payableOrders: readonly TOrder[];
  ordersForCharges: readonly TOrder[];
  loyaltyOrder: TOrder | null;
  enrollmentOfferOrder: TOrder | null;
  orderTotal: number | null;
  orderTaxes: number;
  total: number;
};

const payableOrderStatuses = new Set(["DRAFT", "OPEN", "PARTIALLY_PAID"]);

/**
 * Derives the order, loyalty, and totals state shared by event-signup checkout
 * surfaces. Keeping this calculation in one place prevents the payment card,
 * loyalty, and summary surfaces from disagreeing about which orders are due.
 */
export const getEventSignupCheckoutState = <
  TOrder extends EventSignupCheckoutOrder
>({
  orders,
  registrationSubtotal,
  isDraftSignUp,
}: Params<TOrder>): State<TOrder> => {
  const payableOrders = orders.filter(
    (order) => order.status != null && payableOrderStatuses.has(order.status)
  );
  const ordersForCharges = isDraftSignUp ? payableOrders : orders;
  const checkoutOrder = payableOrders[0] ?? null;
  const enrollmentOfferOrder =
    checkoutOrder?.loyaltyEnrollmentOffer != null ? checkoutOrder : null;
  const loyaltyOrder =
    checkoutOrder?.availableLoyaltyRewards != null || enrollmentOfferOrder
      ? checkoutOrder
      : null;
  const orderTotal = ordersForCharges.length
    ? ordersForCharges.reduce((sum, order) => sum + (order.total ?? 0), 0)
    : null;
  const orderTaxes = ordersForCharges.reduce(
    (sum, order) => sum + (order.totalTaxes ?? 0),
    0
  );

  return {
    payableOrders,
    ordersForCharges,
    loyaltyOrder,
    enrollmentOfferOrder,
    orderTotal,
    orderTaxes,
    total:
      orderTotal ?? (orders.length > 0 ? 0 : registrationSubtotal + orderTaxes),
  };
};
